export type EventStatus = "upcoming" | "ongoing" | "completed";

/** Mirrors Backend's CategoryResponse wire shape. */
export interface CategoryResponse {
  id: string;
  name: string;
  type: string;
  icon_url: string | null;
  icon_hover_url: string;
}

export interface EventCampaignAssociationResponse {
  id: string;
  name: string;
  programId: string;
  programName: string;
}

/** Mirrors Backend's EventItemResponse wire shape. */
export interface EventItemResponse {
  id: string;
  title: string;
  description: string;
  event_date: string | null;
  start_time: string | null;
  check_in_time: string | null;
  end_time: string | null;
  image_url: string | null;
  location: string | null;
  latitude: number;
  longitude: number;
  radius: number;
  time_zone: string | null;
  points: number | null;
  check_in_qr_image: string;
}

/** Mirrors Backend's EventListForAdminResponse wire shape. */
export interface EventListForAdminResponse extends EventItemResponse {
  category: CategoryResponse | null;
  interests: CategoryResponse[];
  attending_count: number;
  checkin_count: number;
  campaign?: EventCampaignAssociationResponse | null;
}

/** Mirrors Backend's EventDetailForAdminResponse wire shape. */
export interface EventDetailForAdminResponse extends EventItemResponse {
  category_id: string;
  category_name: string | null;
  interest_ids: string[];
  guest_attendance_verification: string | null;
  organization_id: string;
  neighborhood_id: string;
  event_status: EventStatus;
  created_at: string;
  updated_at: string;
  campaign?: EventCampaignAssociationResponse | null;
}

/** Mirrors Backend's EventStatisticsResponse wire shape. */
export interface EventStatisticsResponse {
  total_event_count: number;
  upcoming_count: number;
  ongoing_count: number | null;
  completed_count: number | null;
}

/** Mirrors Backend's GetEventListForAdminRequest query contract. */
export interface GetEventListForAdminRequest {
  title?: string;
  keyword?: string;
  event_date_from?: string;
  event_date_to?: string;
  category_id?: string;
  min_points?: number;
  max_points?: number;
  location_name?: string;
  time_start?: string;
  time_end?: string;
  attending_count_start?: number;
  attending_count_end?: number;
  check_in_count_start?: number;
  check_in_end?: number;
  event_status?: EventStatus[];
  status?: string;
  index: number;
  size: number;
  sort_key?: string;
  sort_direction?: "asc" | "desc";
}

/** Mirrors Backend's CreateEventForAdminRequest form contract. */
export interface CreateEventForAdminRequest {
  title: string;
  description?: string;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  radius?: number | null;
  event_date: string;
  start_time: string;
  end_time: string;
  check_in_time?: string;
  event_image_file?: File | null;
  guest_attendance_verification?: string;
  timezone: string;
  image_url?: string;
  category_id?: string;
  sub_category_ids?: string[];
  points?: number | null;
  neighborhood_id?: string;
}

/** Mirrors Backend's UpdateEventForAdminRequest form contract. */
export interface UpdateEventForAdminRequest extends CreateEventForAdminRequest {
  interest_ids?: string[];
  sub_category_ids?: string[];
}

/** Mirrors Backend's scanned-attendees query contract. */
export interface ScannedAttendeeListRequest {
  index?: number;
  size?: number;
}

/** Mirrors Backend's ScannedAttendeeResponse wire shape. */
export interface ScannedAttendeeResponse {
  name: string;
  email: string;
  checked_in_at: string;
}

export interface DropdownOption {
  label: string;
  value: string;
}

export interface LocationDetail {
  country?: string;
  state?: string;
  county?: string;
  city?: string;
  district?: string;
  street?: string;
  name?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  display_name?: string;
  extent?: number[];
}

/**
 * Presentation-only filter state for the portal event-list UI.
 * The owning hook translates this into GetEventListForAdminRequest;
 * the API layer never maps UI state.
 */
export interface EventFilters {
  title: string;
  event_date: {
    from: string;
    to: string;
  };
  category_id: string;
  interest_id: string;
  location: string;
  event_time: {
    from: string;
    to: string;
  };
  points: {
    min: number | null;
    max: number | null;
  };
  attending: {
    min: number | null;
    max: number | null;
  };
  checkin: {
    min: number | null;
    max: number | null;
  };
  status: EventStatus[];
}

export const defaultEventFilter: EventFilters = {
  title: "",
  event_date: {
    from: "",
    to: "",
  },
  category_id: "",
  interest_id: "",
  location: "",
  event_time: {
    from: "",
    to: "",
  },
  points: {
    min: null,
    max: null,
  },
  attending: {
    min: null,
    max: null,
  },
  checkin: {
    min: null,
    max: null,
  },
  status: [],
};

/**
 * Public web flow event shape served by the public `/v1/event` endpoint.
 * External ownership: this shape is not verified against the portal
 * EventController contract and must not gain portal DTO fields.
 */
export interface PublicEventItem {
  id: string;
  title: string;
  description?: string | null;
  event_date: string;
  start_time: string;
  end_time?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  time_zone?: string | null;
  image_url?: string | null;
  categories?: Array<{ id: string; name: string }>;
  interests?: Array<{ id: string; name: string }>;
  organization_population_id?: string | null;
  neighborhood_id?: string | null;
  category_ids?: string[];
  sub_category_ids?: string[];
}
